import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { CalendarDays, Award, ChevronRight, AlertCircle } from "lucide-react";

// Format MySQL date (YYYY-MM-DD) to readable string
const formatDate = (dateStr) => {
  if (!dateStr) return "No Date";
  const options = { year: "numeric", month: "long", day: "numeric" };
  return new Date(dateStr).toLocaleDateString(undefined, options);
};

const Events = () => {
  const [eventsList, setEventsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Initial Fetch
    fetch(`${process.env.REACT_APP_API_URL || "http://localhost:5000"}/api/events`)
      .then((res) => {
        if (!res.ok) throw new Error("Network response was not ok");
        return res.json();
      })
      .then((data) => {
        console.log("Fetched events:", data);
        setEventsList(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setError("No Events Available Right Now.");//Oops! Unable to fetch events. Please try again later.


        setLoading(false);
      });
  }, []);

  const container = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <section
      id="events"
      className="relative w-full py-12 sm:py-16 md:py-20 bg-gradient-to-br from-slate-50 via-white to-blue-50 overflow-hidden"
    >
      <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6">
        {/* Hero Section */}
        <motion.div
          className="text-center mb-12 sm:mb-16 md:mb-20"
          initial={{ opacity: 0, y: -40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-4 sm:mb-6 leading-tight px-4">
            Stay Updated with{" "}
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Campus Life
            </span>
          </h1>
          <p className="max-w-2xl mx-auto text-sm sm:text-base md:text-lg text-gray-600 px-4">
            Explore the latest events, activities, and programs happening across
            campus. Don't miss a chance to participate and connect.
          </p>
        </motion.div>

        {/* Events Header */}
        <motion.div
          className="text-center mb-10 sm:mb-12 md:mb-14"
          initial={{ opacity: 0, y: -30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center px-4 sm:px-5 py-2 bg-blue-100 text-blue-700 rounded-full text-xs sm:text-sm font-semibold mb-4 sm:mb-5 shadow-sm">
            <Award className="w-4 h-4 mr-2" />
            Upcoming Events
          </div>
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 px-4">
            What's <span className="text-blue-600">Happening</span> Soon?
          </h2>
        </motion.div>

        {/* Events Grid */}
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid gap-6 sm:gap-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 w-full"
        >
          {/* Loader */}
          {loading &&
            Array(3)
              .fill(0)
              .map((_, i) => <SkeletonCard key={i} />)}

          {/* Error */}
          {!loading && error && (
            <div className="col-span-full flex flex-col items-center justify-center py-12">
              <AlertCircle className="w-10 h-10 sm:w-12 sm:h-12 text-red-500 mb-4" />
              <p className="text-base sm:text-lg font-semibold text-gray-700 text-center px-4">
                {error}
              </p>
            </div>
          )}

          {/* Events */}
          {!loading &&
            !error &&
            (Array.isArray(eventsList) && eventsList.length > 0 ? (
              eventsList.map((event) => (
                <GlowCard key={event.id} event={event} variants={item} />
              ))
            ) : (
              <div className="col-span-full text-center text-gray-600 py-8 text-sm sm:text-base px-4">
                No events available at the moment.
              </div>
            ))}
        </motion.div>
      </div>
    </section>
  );
};

/* ------------------- Glow Card ------------------- */
const GlowCard = ({ event, variants }) => {
  const [pos, setPos] = useState({ x: 50, y: 50 });

  return (
    <motion.div
      variants={variants}
      whileHover={{ scale: 1.02, y: -8 }}
      className="relative group flex flex-col w-full"
      onMouseMove={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        setPos({ x, y });
      }}
    >
      <div className="relative flex flex-col bg-white/90 backdrop-blur-xl border border-gray-200 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 overflow-hidden h-full">
        {event.image_url ? (
          <img
            src={event.image_url}
            alt={event.title || event.name}
            className="w-full h-48 sm:h-56 md:h-64 object-cover rounded-t-3xl"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-48 sm:h-56 md:h-64 flex items-center justify-center bg-gradient-to-r from-blue-100 to-purple-100 text-blue-600 font-semibold rounded-t-3xl text-sm sm:text-base">
            No Image
          </div>
        )}

        {/* Gradient Overlay on Hover */}
        <div
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition duration-500 pointer-events-none rounded-3xl"
          style={{
            background: `radial-gradient(circle at ${pos.x}% ${pos.y}%, rgba(99,102,241,0.08), transparent 50%)`,
          }}
        />

        {/* Card Content */}
        <div className="relative z-10 flex flex-col items-center text-center p-4 sm:p-6 flex-1">
          <div className="mb-3 sm:mb-4 p-2.5 sm:p-3 bg-gradient-to-br from-blue-50 to-purple-50 text-blue-600 rounded-2xl group-hover:scale-110 transition-transform duration-300">
            <CalendarDays className="w-5 h-5 sm:w-6 sm:h-6" />
          </div>
          <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 mb-2 leading-tight">
            {event.title || event.name}
          </h3>
          <p className="text-gray-600 text-xs sm:text-sm mb-4 line-clamp-3 leading-relaxed">
            {event.description || "Join us for this exciting campus event."}
          </p>
          <div className="mt-auto w-full">
            <span className="inline-flex items-center justify-center px-3 sm:px-4 py-2 rounded-full bg-blue-50 text-blue-700 font-semibold hover:bg-blue-100 transition text-xs sm:text-sm">
              <span className="truncate max-w-full">
                {formatDate(event.event_date || event.date)}
              </span>
              <ChevronRight className="w-4 h-4 ml-1 flex-shrink-0 group-hover:translate-x-1 transition-transform" />
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

/* ------------------- Skeleton Loader ------------------- */
const SkeletonCard = () => (
  <div className="animate-pulse relative bg-white/90 backdrop-blur-xl border border-gray-200 rounded-3xl shadow-lg h-full flex flex-col w-full">
    <div className="w-full h-48 sm:h-56 md:h-64 bg-gray-200 rounded-t-3xl" />
    <div className="flex flex-col flex-1 p-4 sm:p-6 items-center">
      <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gray-200 rounded-2xl mb-3 sm:mb-4" />
      <div className="h-5 sm:h-6 bg-gray-200 rounded w-3/4 mb-2 sm:mb-3" />
      <div className="h-4 bg-gray-200 rounded w-1/2 mb-3 sm:mb-4" />
      <div className="h-8 bg-gray-200 rounded-full w-2/3" />
    </div>
  </div>
);

export default Events;